import { codeService } from "@/lib/code-service"
import type { GroupedCodeValues } from "@/types/code-values"

declare global {
  interface Window {
    PhoenixCodeValues?: GroupedCodeValues
  }
}

/**
 * Module-level fetch deduper for the global code-values lookup table
 * (`window.PhoenixCodeValues`). The table is one-per-session, app-wide, and
 * shared across every WorkflowProvider mount — including the nested provider
 * each VMS sub-tab spins up (vms-sub-tab-content.tsx). Without this deduper,
 * every nested provider would fire its own `/api/code` request on first
 * render because its useEffect's guards read LOCAL React state that's fresh
 * on each mount.
 *
 * Three states:
 *   1. Already loaded — `window.PhoenixCodeValues` is populated. Return
 *      immediately. Phoenix shell may set this before our microapp mounts,
 *      or an earlier provider in this session may have finished the fetch.
 *   2. In flight — another caller's fetch is pending. Wait on the shared
 *      promise so only one network request goes out, then return.
 *   3. Cold — kick off a fetch, store it on the module-level handle so
 *      concurrent callers can piggyback, write the result to the window
 *      global, and emit `phoenixCodeValuesLoaded` to wake up any listener
 *      polling for readiness.
 *
 * On failure the in-flight handle is cleared so the next mount retries
 * rather than getting stuck observing a dead promise. Token is taken per
 * call because it can rotate mid-session.
 */
let inFlight: Promise<GroupedCodeValues> | null = null

export async function ensureCodeValuesLoaded(token: string | null): Promise<void> {
  if (typeof window === "undefined") return

  // Fast path — the global is already populated. Common case on every
  // mount after the first.
  if (window.PhoenixCodeValues) return

  // In-flight path — first caller is fetching, piggyback rather than
  // racing a second request.
  if (inFlight) {
    try {
      await inFlight
    } catch {
      // The originator already handled the error + cleared inFlight. Swallow
      // here so a piggybacking caller's mount effect doesn't unwind on a
      // failure that's not its responsibility.
    }
    return
  }

  inFlight = (async () => {
    try {
      const data = await codeService.getCodeValues(token)
      if (typeof window !== "undefined") {
        window.PhoenixCodeValues = data
        // Custom event mirrors the legacy notification used by listeners
        // polling for readiness (see workflow-context-simple.setCodeValues).
        // Keeps anything still subscribed to it functioning.
        window.dispatchEvent(
          new CustomEvent("phoenixCodeValuesLoaded", { detail: data }),
        )
      }
      return data
    } catch (err) {
      // Clear the handle on failure so the next mount can retry. If we
      // left it set, every subsequent caller would await a rejected
      // promise and silently fail with the same error indefinitely.
      inFlight = null
      throw err
    }
  })()

  try {
    await inFlight
  } catch {
    // Same swallow as above — the originator threw and cleared inFlight;
    // piggybacked callers shouldn't propagate.
  }
}

/**
 * Test-only escape hatch — resets the in-flight handle so unit tests can
 * exercise the cold-load path without cross-test pollution. Never call from
 * application code.
 */
export function __resetCodeValuesLoaderForTests(): void {
  inFlight = null
}
